import React, { useState, useEffect } from 'react';
import { apiRequest } from '../utils/api';
import CreateSubscription from './CreateSubscription';
import EnvModal from './EnvModal';
import EditCustomer from './EditCustomer';
import SetupChecklist from './SetupChecklist';

// Helper component for displaying information fields
const InfoField = ({ label, value, code = false, url = false, email = false, secret = false, multiline = false, copyable = false }) => {
  const [copied, setCopied] = React.useState(false);
  const [showSecret, setShowSecret] = React.useState(false);

  if (!value && value !== 0) return null;

  const handleCopy = async (textToCopy) => {
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      alert('فشل نسخ البيانات');
    }
  };

  let displayValue = value;
  const isSecret = secret && typeof value === 'string' && value.length > 0;
  if (isSecret && !showSecret) {
    displayValue = '••••••••';
  }

  const content = (
    <>
      {url && value ? (
        <a href={value} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-600)', textDecoration: 'none', fontWeight: 500 }}>
          {displayValue} ↗
        </a>
      ) : email && value ? (
        <a href={`mailto:${value}`} style={{ color: 'var(--primary-600)', textDecoration: 'none', fontWeight: 500 }}>
          {displayValue}
        </a>
      ) : code ? (
        <div style={{ position: 'relative' }}>
          <code style={{
            display: 'block',
            fontSize: '0.8125rem',
            fontFamily: 'monospace',
            backgroundColor: 'var(--gray-50)',
            padding: '0.5rem',
            paddingRight: copyable || isSecret ? '2.5rem' : '0.5rem',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--gray-200)',
            wordBreak: 'break-all',
            color: 'var(--gray-900)'
          }}>
            {displayValue}
          </code>
          {(copyable || isSecret) && (
            <div style={{ position: 'absolute', left: '0.5rem', top: '0.5rem', display: 'flex', gap: '0.5rem' }}>
              {isSecret && (
                <button
                  onClick={() => setShowSecret(!showSecret)}
                  style={{
                    background: 'transparent',
                    border: '1px solid var(--gray-300)',
                    borderRadius: 'var(--radius-sm)',
                    padding: '0.25rem 0.5rem',
                    fontSize: '0.75rem',
                    cursor: 'pointer',
                    color: 'var(--gray-700)',
                    fontWeight: 500
                  }}
                >
                  {showSecret ? '👁️ إخفاء' : '👁️ عرض'}
                </button>
              )}
              <button
                onClick={() => handleCopy(value)}
                style={{
                  background: copied ? 'var(--success-600)' : 'var(--primary-600)',
                  border: 'none',
                  borderRadius: 'var(--radius-sm)',
                  padding: '0.25rem 0.5rem',
                  fontSize: '0.75rem',
                  cursor: 'pointer',
                  color: 'white',
                  fontWeight: 500,
                  transition: 'background-color 0.2s'
                }}
              >
                {copied ? '✓ تم النسخ' : '📋 نسخ'}
              </button>
            </div>
          )}
        </div>
      ) : multiline ? (
        <div style={{ fontSize: '0.875rem', color: 'var(--gray-700)', whiteSpace: 'pre-wrap', lineHeight: '1.6', position: 'relative' }}>
          {displayValue}
          {(copyable || isSecret) && (
            <div style={{ marginTop: '0.5rem' }}>
              {isSecret && (
                <button
                  onClick={() => setShowSecret(!showSecret)}
                  className="btn btn-secondary btn-sm"
                  style={{ marginLeft: '0.5rem' }}
                >
                  {showSecret ? '👁️ إخفاء' : '👁️ عرض'}
                </button>
              )}
              <button
                onClick={() => handleCopy(value)}
                className={copied ? 'btn btn-success btn-sm' : 'btn btn-primary btn-sm'}
              >
                {copied ? '✓ تم النسخ' : '📋 نسخ'}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div style={{ fontWeight: 500, color: 'var(--gray-900)', wordBreak: 'break-word', position: 'relative' }}>
          {displayValue}
          {(copyable || isSecret) && (
            <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem' }}>
              {isSecret && (
                <button
                  onClick={() => setShowSecret(!showSecret)}
                  className="btn btn-secondary btn-sm"
                >
                  {showSecret ? '👁️ إخفاء' : '👁️ عرض'}
                </button>
              )}
              <button
                onClick={() => handleCopy(value)}
                className={copied ? 'btn btn-success btn-sm' : 'btn btn-primary btn-sm'}
              >
                {copied ? '✓ تم النسخ' : '📋 نسخ'}
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );

  return (
    <div>
      <div style={{ fontSize: '0.8125rem', color: 'var(--gray-600)', marginBottom: '0.5rem', fontWeight: 500 }}>
        {label}
      </div>
      {content}
    </div>
  );
};

// Helper component for displaying color fields
const ColorField = ({ label, color, hsl }) => {
  if (!color) return null;

  return (
    <div>
      <div style={{ fontSize: '0.8125rem', color: 'var(--gray-600)', marginBottom: '0.5rem', fontWeight: 500 }}>
        {label}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <div style={{
          width: '50px',
          height: '50px',
          backgroundColor: color,
          borderRadius: 'var(--radius-md)',
          border: '2px solid var(--gray-300)',
          flexShrink: 0,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}></div>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: 'monospace', fontSize: '0.875rem', fontWeight: 600, color: 'var(--gray-900)', marginBottom: '0.25rem' }}>
            {color}
          </div>
          {hsl && (
            <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', fontFamily: 'monospace' }}>
              HSL: {hsl}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const CustomerDetails = ({ customer, onBack, onRefresh }) => {
  const [customerData, setCustomerData] = useState(customer);
  const [usageLogs, setUsageLogs] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [creditForm, setCreditForm] = useState({
    creditType: 'aiCredits',
    operation: 'add',
    amount: '',
    reference: '',
  });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [envModal, setEnvModal] = useState({ isOpen: false, title: '', content: '' });
  const [editMode, setEditMode] = useState(false);
  const [refreshingOpenAI, setRefreshingOpenAI] = useState(false);
  const [apiKeyCopied, setApiKeyCopied] = useState(false);
  const [showApiToken, setShowApiToken] = useState(false);

  useEffect(() => {
    fetchCustomerDetails();
    fetchUsageLogs();
    fetchTransactions();
    fetchSubscription();
  }, [customer._id]);

  const fetchCustomerDetails = async () => {
    try {
      const data = await apiRequest(`/admin/customers/${customer._id}`);
      if (data && data.success) {
        setCustomerData(data.data);
      }
    } catch (error) {
      console.error('Error fetching customer details:', error);
    }
  };

  const fetchUsageLogs = async () => {
    setLoading(true);
    try {
      const data = await apiRequest(`/admin/customers/${customer._id}/usage-logs?limit=50`);
      if (data && data.success) {
        setUsageLogs(data.data);
      }
    } catch (error) {
      console.error('Error fetching usage logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      const data = await apiRequest(`/admin/customers/${customer._id}/transactions?limit=50`);
      if (data && data.success) {
        setTransactions(data.data);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
    }
  };

  const fetchSubscription = async () => {
    try {
      const data = await apiRequest(`/admin/customers/${customer._id}/subscription`);
      if (data && data.success) {
        setSubscription(data.data);
      } else {
        setSubscription(null);
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
      setSubscription(null);
    }
  };

  const generateBackendEnv = () => {
    const c = customerData;
    const storeName = c.storeNameEn || c.name || 'Store';
    
    let env = `# ============================================
# ${storeName} Backend Environment Variables
# ============================================
# Copy this file to Backend/.env and update with your values
# All values are used throughout the backend system

# ============================================
# Server Configuration
# ============================================
NODE_ENV=development
PORT=5000

# Public URLs (for webhooks, callbacks, etc.)
API_PUBLIC_URL=${c.apiUrl || 'http://localhost:5000'}

# ============================================
# Database Configuration
# ============================================
MONGODB_URI=${c.mongodbUri || 'mongodb://localhost:27017/tijaraplus'}
MONGODB_DB_NAME=${c.mongodbDbName || 'tijaraplus'}

# ============================================
# JWT Authentication
# ============================================
JWT_SECRET=dskjvbskdjvnbskdbvlksdvnlsdnvlksdvn
JWT_EXPIRE=7d

# ============================================
# Store Information - Names
# ============================================
STORE_NAME_EN=${c.storeNameEn || ''}
STORE_NAME_AR=${c.storeNameAr || ''}
STORE_NAME_SHORT_EN=${c.storeNameShortEn || ''}
STORE_NAME_SHORT_AR=${c.storeNameShortAr || ''}

# ============================================
# Store Information - Contact
# ============================================
STORE_EMAIL_INFO=${c.storeEmailInfo || ''}
STORE_EMAIL_SUPPORT=${c.storeEmailSupport || ''}
STORE_EMAIL_ADMIN=${c.storeEmailAdmin || ''}
STORE_EMAIL_PRIVACY=${c.storeEmailPrivacy || ''}
STORE_PHONE_PRIMARY=${c.storePhonePrimary || ''}
STORE_PHONE_SECONDARY=${c.storePhoneSecondary || ''}

# Store Information - Address
STORE_ADDRESS_EN=${c.storeAddressEn || ''}
STORE_ADDRESS_AR=${c.storeAddressAr || ''}
STORE_ADDRESS_LINE1=${c.storeAddressEn || ''}
STORE_ADDRESS_LINE2=${c.storeAddressLine2 || ''}
STORE_COUNTRY=${c.storeCountry || ''}
STORE_CITY=${c.storeCity || ''}

# ============================================
# Store Information - URLs
# ============================================
STORE_FRONTEND_URL=${c.storeFrontendUrl || 'http://localhost:8080'}
STORE_CDN_URL=${c.r2PublicBaseUrl || c.r2PublicUrl || 'https://cdn.example.com'}
STORE_LOGO_URL=/logo.png
STORE_FAVICON_URL=/favicon.ico

# ============================================
# Store Information - Descriptions
# ============================================
STORE_DESCRIPTION_EN=${c.storeDescriptionEn || ''}
STORE_DESCRIPTION_AR=${c.storeDescriptionAr || ''}

STORE_TAGLINE_EN=${c.storeTaglineEn || ''}
STORE_TAGLINE_AR=${c.storeTaglineAr || ''}

# ============================================
# Store Information - Working Hours
# ============================================
STORE_HOURS_WEEKDAYS_EN=${c.storeHoursWeekdaysEn || ''}
STORE_HOURS_WEEKDAYS_AR=${c.storeHoursWeekdaysAr || ''}
STORE_HOURS_WEEKEND_EN=${c.storeHoursWeekendEn || ''}
STORE_HOURS_WEEKEND_AR=${c.storeHoursWeekendAr || ''}

# ============================================
# Store Information - Brand Colors
# ============================================
# Primary brand color: main buttons, links, highlights
STORE_PRIMARY_COLOR=${c.storePrimaryColor || '#1E6FFB'}
STORE_PRIMARY_COLOR_HSL=${c.storePrimaryColorHsl || '210 96% 49%'}

# Secondary color: secondary buttons, badges, subtle highlights
STORE_SECONDARY_COLOR=${c.storeSecondaryColor || '#0D3B73'}
STORE_SECONDARY_COLOR_HSL=${c.storeSecondaryColorHsl || '210 78% 23%'}

# Accent color: icons, small elements, calls to action
STORE_ACCENT_COLOR=${c.storeAccentColor || '#00C3FF'}
STORE_ACCENT_COLOR_HSL=${c.storeAccentColorHsl || '190 100% 50%'}

# Background color: page backgrounds
STORE_BACKGROUND_COLOR=${c.storeBackgroundColor || '#F3F7FF'}
STORE_BACKGROUND_COLOR_HSL=${c.storeBackgroundColorHsl || '220 100% 97%'}

# ============================================
# Cloudflare R2 Storage
# ============================================
R2_ACCESS_KEY_ID=${c.r2AccessKeyId || ''}
R2_SECRET_ACCESS_KEY=${c.r2SecretAccessKey || ''}
R2_BUCKET_NAME=${c.r2BucketName || ''}
R2_PUBLIC_URL=${c.r2PublicUrl || ''}
R2_ENDPOINT=${c.r2Endpoint || ''}

# Alternative R2/CDN URL variables (used as fallbacks)
R2_PUBLIC_BASE_URL=${c.r2PublicBaseUrl || c.r2PublicUrl || ''}
CLOUDFLARE_PUBLIC_URL=${c.r2PublicBaseUrl || c.r2PublicUrl || ''}
MEDIA_BASE_URL=${c.r2PublicBaseUrl || c.r2PublicUrl || ''}
CDN_BASE_URL=${c.r2PublicBaseUrl || c.r2PublicUrl || ''}

# ============================================
# Email Configuration (SMTP)
# ============================================
SMTP_HOST=smtp-relay.brevo.com
SMTP_PORT=587
SMTP_SECURE=false
SMTP_USER=your-email@smtp-brevo.com
SMTP_PASSWORD=your-smtp-password

# Email From Configuration
EMAIL_FROM_NAME=${c.storeNameEn || 'Store'}
EMAIL_FROM_ADDRESS=${c.storeEmailInfo || 'noreply@example.com'}

# ============================================
# Telegram Bot Configuration
# ============================================
TELEGRAM_BOT_TOKEN=${c.telegramBotToken || ''}
TELEGRAM_BOT_USERNAME=${c.telegramBotUsername || ''}
TELEGRAM_WEBHOOK_PATH=/telegram/webhook

# Telegram Document/File Sharing
TELEGRAM_DOC_TOKEN_SECRET=122b5b63814fdcsdvsdvsdcsdvsdsdv2bc5cd0ac2ba1e47db4da721d4
TELEGRAM_DOC_TOKEN_TTL=7d
TELEGRAM_LINK_SECRET=122b5b63814fdcsdvsdvsdcsdvsdsdv2bc5cd0ac2ba1e47db4da721d4
TELEGRAM_LINK_TTL=7d
TELEGRAM_MEDIA_BASE_URL=${c.r2PublicBaseUrl || c.r2PublicUrl || ''}

# ============================================
# AI Image Generation (Nano Banana)
# ============================================
NANOBANANA_API_KEY=${c.apiToken || customer.apiToken || ''}
NANOBANANA_API_BASE_URL=${c.apiUrl || 'http://localhost:5010'}
AI_IMAGE_CALLBACK_URL=${c.apiUrl ? `${c.apiUrl}/api/ai-image/callback` : 'https://your-domain.com/api/ai-image/callback'}

# ============================================
# OpenAI (for Embeddings)
# ============================================
OPENAI_API_KEY=

# ============================================
# Admin Default Credentials (for initial setup)
# ============================================
# These are used by the createAdmin script to create the first admin user
ADMIN_DEFAULT_EMAIL=${c.adminDefaultEmail || ''}
ADMIN_DEFAULT_PASSWORD=${c.adminDefaultPassword || 'admin123'}
ADMIN_DEFAULT_NAME=${c.adminDefaultName || ''}
ADMIN_DEFAULT_PHONE=${c.adminDefaultPhone || ''}
`;

    return env;
  };

  const generateFrontendEnv = () => {
    const c = customerData;
    const storeName = c.storeNameEn || c.name || 'Store';
    const apiBaseUrl = c.apiUrl || 'https://api.example.com';
    
    let env = `# ============================================
# ${storeName} Frontend Environment Variables
# ============================================
# Copy this file to .env in the root directory and update with your values
# Note: All frontend variables must be prefixed with VITE_ to be accessible in the browser

# ============================================
# API Configuration
# ============================================
# VITE_API_BASE_URL is preferred (includes /api)
# If not set, VITE_API_URL will be used and /api will be appended
VITE_API_URL=${apiBaseUrl}
VITE_API_BASE_URL=${apiBaseUrl}/api

# ============================================
# Store Information - Names
# ============================================
VITE_STORE_NAME_EN=${c.storeNameEn || ''}
VITE_STORE_NAME_AR=${c.storeNameAr || ''}
VITE_STORE_NAME_SHORT_EN=${c.storeNameShortEn || ''}
VITE_STORE_NAME_SHORT_AR=${c.storeNameShortAr || ''}

# ============================================
# Store Information - Contact
# ============================================
VITE_STORE_EMAIL_INFO=${c.storeEmailInfo || ''}
VITE_STORE_EMAIL_SUPPORT=${c.storeEmailSupport || ''}
VITE_STORE_EMAIL_ADMIN=${c.storeEmailAdmin || ''}
VITE_STORE_EMAIL_PRIVACY=${c.storeEmailPrivacy || ''}
VITE_STORE_PHONE_PRIMARY=${c.storePhonePrimary || ''}
VITE_STORE_PHONE_SECONDARY=${c.storePhoneSecondary || ''}

# Store Information - Address
VITE_STORE_ADDRESS_EN=${c.storeAddressEn || ''}
VITE_STORE_ADDRESS_AR=${c.storeAddressAr || ''}
VITE_STORE_ADDRESS_LINE2=${c.storeAddressLine2 || ''}
VITE_STORE_COUNTRY=${c.storeCountry || ''}
VITE_STORE_CITY=${c.storeCity || ''}

# ============================================
# Store Information - URLs
# ============================================
VITE_STORE_FRONTEND_URL=${c.storeFrontendUrl || 'http://localhost:8080'}
VITE_STORE_CDN_URL=${c.r2PublicBaseUrl || c.r2PublicUrl || 'https://cdn.example.com'}
VITE_STORE_LOGO_URL=/logo.png
VITE_STORE_FAVICON_URL=/favicon.ico

# ============================================
# Store Information - Descriptions
# ============================================
VITE_STORE_DESCRIPTION_EN=${c.storeDescriptionEn || ''}
VITE_STORE_DESCRIPTION_AR=${c.storeDescriptionAr || ''}

VITE_STORE_TAGLINE_EN=${c.storeTaglineEn || ''}
VITE_STORE_TAGLINE_AR=${c.storeTaglineAr || ''}

# ============================================
# Store Information - Working Hours
# ============================================
VITE_STORE_HOURS_WEEKDAYS_EN=${c.storeHoursWeekdaysEn || ''}
VITE_STORE_HOURS_WEEKDAYS_AR=${c.storeHoursWeekdaysAr || ''}
VITE_STORE_HOURS_WEEKEND_EN=${c.storeHoursWeekendEn || ''}
VITE_STORE_HOURS_WEEKEND_AR=${c.storeHoursWeekendAr || ''}

# ============================================
# Store Information - Brand Colors
# ============================================
VITE_STORE_PRIMARY_COLOR=${c.storePrimaryColor || '#1E6FFB'}
VITE_STORE_PRIMARY_COLOR_HSL=${c.storePrimaryColorHsl || '210 96% 49%'}

VITE_STORE_SECONDARY_COLOR=${c.storeSecondaryColor || '#0D3B73'}
VITE_STORE_SECONDARY_COLOR_HSL=${c.storeSecondaryColorHsl || '210 78% 23%'}

VITE_STORE_ACCENT_COLOR=${c.storeAccentColor || '#00C3FF'}
VITE_STORE_ACCENT_COLOR_HSL=${c.storeAccentColorHsl || '190 100% 50%'}

VITE_STORE_BACKGROUND_COLOR=${c.storeBackgroundColor || '#F3F7FF'}
VITE_STORE_BACKGROUND_COLOR_HSL=${c.storeBackgroundColorHsl || '220 100% 97%'}

# ============================================
# Coming Soon Page
# ============================================
VITE_STORE_COMING_SOON_DESCRIPTION_EN=${c.storeComingSoonDescriptionEn || ''}
VITE_STORE_COMING_SOON_DESCRIPTION_AR=${c.storeComingSoonDescriptionAr || ''}

VITE_STORE_COMING_SOON_TAGLINE_EN=${c.storeComingSoonTaglineEn || ''}
VITE_STORE_COMING_SOON_TAGLINE_AR=${c.storeComingSoonTaglineAr || ''}

VITE_BUSINESS_TYPE=${c.businessType || ''}
`;

    return env;
  };

  const handleShowBackendEnv = () => {
    const envContent = generateBackendEnv();
    setEnvModal({
      isOpen: true,
      title: 'ملف .env للخادم (Backend)',
      content: envContent
    });
  };

  const handleShowFrontendEnv = () => {
    const envContent = generateFrontendEnv();
    setEnvModal({
      isOpen: true,
      title: 'ملف .env للواجهة الأمامية (Frontend)',
      content: envContent
    });
  };

  const handleRegenerateToken = async () => {
    if (!window.confirm('هل أنت متأكد من إعادة إنشاء رمز API؟ الرمز الحالي سيتوقف عن العمل.')) {
      return;
    }

    try {
      const data = await apiRequest(
        `/admin/customers/${customer._id}/regenerate-token`,
        { method: 'POST' }
      );
      if (data.success) {
        alert(`تم إنشاء رمز API جديد:\n\n${data.data.apiToken}\n\nاحفظ هذا الرمز في مكان آمن.`);
        fetchCustomerDetails();
      } else {
        alert('حدث خطأ: ' + data.message);
      }
    } catch (error) {
      alert('حدث خطأ في الاتصال بالخادم');
    }
  };

  const handleCopyApiKey = () => {
    const apiToken = customerData.apiToken || customer.apiToken;
    if (apiToken) {
      navigator.clipboard.writeText(apiToken).then(() => {
        setApiKeyCopied(true);
        setTimeout(() => setApiKeyCopied(false), 2000);
      }).catch(err => {
        console.error('Failed to copy API key:', err);
        alert('فشل نسخ رمز API');
      });
    }
  };

  const handleRefreshOpenAICosts = async () => {
    setRefreshingOpenAI(true);
    try {
      const data = await apiRequest(`/admin/openai/customers/${customer._id}/refresh`, {
        method: 'POST',
        body: JSON.stringify({ daysBack: 180 }),
      });
      if (data.success) {
        setMessage({ type: 'success', text: data.message || 'تم تحديث تكاليف OpenAI بنجاح' });
        fetchCustomerDetails();
        if (onRefresh) onRefresh();
      } else {
        setMessage({ type: 'error', text: data.message || 'حدث خطأ' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'حدث خطأ في الاتصال بالخادم' });
    } finally {
      setRefreshingOpenAI(false);
    }
  };

  const handleAdjustCredits = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    try {
      const data = await apiRequest(`/admin/customers/${customer._id}/credits`, {
        method: 'POST',
        body: JSON.stringify(creditForm),
      });
      if (data.success) {
        setMessage({ type: 'success', text: 'تم تعديل الرصيد بنجاح' });
        setCreditForm({ ...creditForm, amount: '', reference: '' });
        fetchCustomerDetails();
        if (onRefresh) onRefresh();
      } else {
        setMessage({ type: 'error', text: data.message || 'حدث خطأ' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'حدث خطأ في الاتصال بالخادم' });
    }
  };

  if (editMode) {
    return (
      <EditCustomer
        customer={customerData}
        onUpdate={(updatedCustomer) => {
          setCustomerData(updatedCustomer);
          setEditMode(false);
          if (onRefresh) onRefresh();
        }}
        onCancel={() => setEditMode(false)}
      />
    );
  }

  const apiToken = customerData.apiToken || customer.apiToken;

  return (
    <div>
      {/* Header Section with Customer Info */}
      <div className="card" style={{ marginBottom: '1.5rem', background: 'linear-gradient(135deg, var(--primary-600) 0%, var(--primary-800) 100%)', color: 'white', border: 'none' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1.5rem' }}>
          <div style={{ flex: 1, minWidth: '300px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
              <button className="btn btn-secondary btn-sm" onClick={onBack} style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)', borderColor: 'rgba(255, 255, 255, 0.3)', color: 'white' }}>
                ← رجوع
              </button>
            </div>
            <h1 style={{ fontSize: '2rem', fontWeight: 700, marginBottom: '0.5rem', color: 'white' }}>
              {customerData.name || 'بدون اسم'}
            </h1>
            <p style={{ fontSize: '1rem', opacity: 0.9, marginBottom: '1rem' }}>
              {customerData.email}
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <span className={`badge ${customerData.isActive ? 'badge-success' : 'badge-danger'}`} style={{ backgroundColor: customerData.isActive ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)', color: 'white', border: '1px solid rgba(255, 255, 255, 0.3)' }}>
                {customerData.isActive ? '✓ نشط' : '✗ غير نشط'}
              </span>
              {customerData.businessType && (
                <span className="badge" style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)', color: 'white', border: '1px solid rgba(255, 255, 255, 0.3)' }}>
                  {customerData.businessType === 'electronics' ? 'إلكترونيات' :
                   customerData.businessType === 'clothes' ? 'ملابس' :
                   customerData.businessType === 'home-accessories' ? 'إكسسوارات منزلية' : customerData.businessType}
                </span>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <button className="btn btn-primary btn-sm" onClick={() => setEditMode(true)} style={{ backgroundColor: 'white', color: 'var(--primary-600)', border: 'none' }}>
              ✏️ تعديل
            </button>
            {apiToken && (
              <button className="btn btn-primary btn-sm" onClick={handleCopyApiKey} style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)', borderColor: 'rgba(255, 255, 255, 0.3)', color: 'white' }}>
                {apiKeyCopied ? '✓ تم النسخ' : '📋 نسخ API Key'}
              </button>
            )}
            {customerData.openaiProjectId && (
              <button
                className="btn btn-primary btn-sm"
                onClick={handleRefreshOpenAICosts}
                disabled={refreshingOpenAI}
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)', borderColor: 'rgba(255, 255, 255, 0.3)', color: 'white' }}
              >
                {refreshingOpenAI ? '⏳ جاري التحديث...' : '🔄 تحديث OpenAI'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Balance Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
        <div className="card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)', marginBottom: '0.5rem', fontWeight: 500 }}>رصيد AI</div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--primary-600)' }}>
            {customerData.balances?.aiCredits?.toLocaleString() || '0'}
          </div>
        </div>
        <div className="card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)', marginBottom: '0.5rem', fontWeight: 500 }}>رصيد SMS</div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--info)' }}>
            {customerData.balances?.smsCredits?.toLocaleString() || '0'}
          </div>
        </div>
        <div className="card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)', marginBottom: '0.5rem', fontWeight: 500 }}>رصيد OpenAI</div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--success)' }}>
            {customerData.balances?.openaiBalance?.toLocaleString() || '0'}
          </div>
        </div>
      </div>

      {message.text && (
        <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-danger'}`} style={{ marginBottom: '1.5rem' }}>
          <span>{message.type === 'success' ? '✅' : '❌'}</span>
          <span>{message.text}</span>
        </div>
      )}

      {/* Credit Adjustment Card */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-header">
          <h3 className="card-title">تعديل الرصيد</h3>
        </div>
        <form onSubmit={handleAdjustCredits}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
            <div className="form-group">
              <label className="form-label">نوع العملية</label>
              <select
                className="form-control"
                value={creditForm.operation}
                onChange={(e) => setCreditForm({ ...creditForm, operation: e.target.value })}
              >
                <option value="add">➕ إضافة رصيد</option>
                <option value="deduct">➖ خصم رصيد</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">نوع الرصيد</label>
              <select
                className="form-control"
                value={creditForm.creditType}
                onChange={(e) => setCreditForm({ ...creditForm, creditType: e.target.value })}
              >
                <option value="aiCredits">رصيد AI ({customerData.balances?.aiCredits || 0})</option>
                <option value="smsCredits">رصيد SMS ({customerData.balances?.smsCredits || 0})</option>
                <option value="openaiBalance">رصيد OpenAI ({customerData.balances?.openaiBalance || 0})</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">المبلغ</label>
              <input
                type="number"
                className="form-control"
                value={creditForm.amount}
                onChange={(e) => setCreditForm({ ...creditForm, amount: e.target.value })}
                min="0"
                step="0.01"
                required
                placeholder="أدخل المبلغ"
              />
            </div>
            <div className="form-group">
              <label className="form-label">المرجع (اختياري)</label>
              <input
                type="text"
                className="form-control"
                value={creditForm.reference}
                onChange={(e) => setCreditForm({ ...creditForm, reference: e.target.value })}
                placeholder="مثال: دفع فاتورة"
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button 
              type="submit" 
              className={`btn ${creditForm.operation === 'add' ? 'btn-success' : 'btn-danger'}`}
            >
              {creditForm.operation === 'add' ? 'إضافة رصيد' : 'خصم رصيد'}
            </button>
          </div>
        </form>
      </div>

      {/* Main Content Tabs */}
      <div className="card">
        <div className="tabs">
          <button
            className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            📊 نظرة عامة
          </button>
          <button
            className={`tab-button ${activeTab === 'subscription' ? 'active' : ''}`}
            onClick={() => setActiveTab('subscription')}
          >
            📋 الاشتراك
          </button>
          <button
            className={`tab-button ${activeTab === 'logs' ? 'active' : ''}`}
            onClick={() => setActiveTab('logs')}
          >
            📝 سجل الاستخدام
          </button>
          <button
            className={`tab-button ${activeTab === 'transactions' ? 'active' : ''}`}
            onClick={() => setActiveTab('transactions')}
          >
            💳 المعاملات
          </button>
          <button
            className={`tab-button ${activeTab === 'configuration' ? 'active' : ''}`}
            onClick={() => setActiveTab('configuration')}
          >
            ⚙️ الإعدادات
          </button>
          <button
            className={`tab-button ${activeTab === 'checklist' ? 'active' : ''}`}
            onClick={() => setActiveTab('checklist')}
          >
            ✅ قائمة الإعداد
          </button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
              {/* Basic Information */}
              <div>
                <h4 style={{ marginBottom: '1rem', color: 'var(--primary-600)', fontSize: '1rem', fontWeight: 600, paddingBottom: '0.75rem', borderBottom: '2px solid var(--gray-200)' }}>
                  المعلومات الأساسية
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  <div>
                    <div style={{ fontSize: '0.8125rem', color: 'var(--gray-600)', marginBottom: '0.25rem' }}>الاسم</div>
                    <div style={{ fontWeight: 500, color: 'var(--gray-900)' }}>{customerData.name || '—'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.8125rem', color: 'var(--gray-600)', marginBottom: '0.25rem' }}>البريد الإلكتروني</div>
                    <div style={{ fontWeight: 500, color: 'var(--gray-900)' }}>{customerData.email || '—'}</div>
                  </div>
                  {customerData.storeNameEn && (
                    <div>
                      <div style={{ fontSize: '0.8125rem', color: 'var(--gray-600)', marginBottom: '0.25rem' }}>اسم المتجر (EN)</div>
                      <div style={{ fontWeight: 500, color: 'var(--gray-900)' }}>{customerData.storeNameEn}</div>
                    </div>
                  )}
                  {customerData.storeNameAr && (
                    <div>
                      <div style={{ fontSize: '0.8125rem', color: 'var(--gray-600)', marginBottom: '0.25rem' }}>اسم المتجر (AR)</div>
                      <div style={{ fontWeight: 500, color: 'var(--gray-900)' }}>{customerData.storeNameAr}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Store URLs */}
              {(customerData.apiUrl || customerData.storeFrontendUrl) && (
                <div>
                  <h4 style={{ marginBottom: '1rem', color: 'var(--primary-600)', fontSize: '1rem', fontWeight: 600, paddingBottom: '0.75rem', borderBottom: '2px solid var(--gray-200)' }}>
                    الروابط
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {customerData.apiUrl && (
                      <div>
                        <div style={{ fontSize: '0.8125rem', color: 'var(--gray-600)', marginBottom: '0.25rem' }}>API URL</div>
                        <div style={{ fontFamily: 'monospace', fontSize: '0.875rem', backgroundColor: 'var(--gray-50)', padding: '0.5rem', borderRadius: 'var(--radius-md)', wordBreak: 'break-all' }}>
                          {customerData.apiUrl}
                        </div>
                      </div>
                    )}
                    {customerData.storeFrontendUrl && (
                      <div>
                        <div style={{ fontSize: '0.8125rem', color: 'var(--gray-600)', marginBottom: '0.25rem' }}>رابط الواجهة الأمامية</div>
                        <a href={customerData.storeFrontendUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-600)', textDecoration: 'none', fontWeight: 500 }}>
                          {customerData.storeFrontendUrl} ↗
                        </a>
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                      <button className="btn btn-primary btn-sm" onClick={handleShowBackendEnv}>
                        ملف .env للخادم
                      </button>
                      <button className="btn btn-primary btn-sm" onClick={handleShowFrontendEnv}>
                        ملف .env للواجهة
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* API Token */}
              {apiToken && (
                <div>
                  <h4 style={{ marginBottom: '1rem', color: 'var(--primary-600)', fontSize: '1rem', fontWeight: 600, paddingBottom: '0.75rem', borderBottom: '2px solid var(--gray-200)' }}>
                    رمز API
                  </h4>
                  <div style={{ backgroundColor: 'var(--gray-50)', padding: '1rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--gray-200)', position: 'relative' }}>
                    <code style={{ 
                      display: 'block', 
                      fontSize: '0.8125rem', 
                      wordBreak: 'break-all',
                      fontFamily: 'monospace',
                      color: 'var(--gray-900)',
                      marginBottom: '0.75rem',
                      paddingRight: '6rem'
                    }}>
                      {showApiToken ? apiToken : '••••••••'}
                    </code>
                    <div style={{ position: 'absolute', left: '1rem', top: '1rem', display: 'flex', gap: '0.5rem' }}>
                      <button
                        onClick={() => setShowApiToken(!showApiToken)}
                        style={{
                          background: 'transparent',
                          border: '1px solid var(--gray-300)',
                          borderRadius: 'var(--radius-sm)',
                          padding: '0.25rem 0.5rem',
                          fontSize: '0.75rem',
                          cursor: 'pointer',
                          color: 'var(--gray-700)',
                          fontWeight: 500
                        }}
                      >
                        {showApiToken ? '👁️ إخفاء' : '👁️ عرض'}
                      </button>
                      <button
                        onClick={handleCopyApiKey}
                        style={{
                          background: apiKeyCopied ? 'var(--success-600)' : 'var(--primary-600)',
                          border: 'none',
                          borderRadius: 'var(--radius-sm)',
                          padding: '0.25rem 0.5rem',
                          fontSize: '0.75rem',
                          cursor: 'pointer',
                          color: 'white',
                          fontWeight: 500,
                          transition: 'background-color 0.2s'
                        }}
                      >
                        {apiKeyCopied ? '✓ تم النسخ' : '📋 نسخ'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Subscription Tab */}
        {activeTab === 'subscription' && (
          <div>
            {subscription ? (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
                  <div>
                    <div style={{ fontSize: '0.8125rem', color: 'var(--gray-600)', marginBottom: '0.5rem' }}>الباقة</div>
                    <div style={{ fontWeight: 600, fontSize: '1.125rem' }}>
                      {subscription.package === 'basic' ? 'أساسي' :
                       subscription.package === 'standard' ? 'قياسي' :
                       subscription.package === 'premium' ? 'مميز' : subscription.package}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.8125rem', color: 'var(--gray-600)', marginBottom: '0.5rem' }}>الحالة</div>
                    <div>
                      {new Date(subscription.endDate) < new Date() ? (
                        <span className="badge badge-danger">منتهي</span>
                      ) : subscription.status === 'active' ? (
                        <span className="badge badge-success">نشط</span>
                      ) : subscription.status === 'cancelled' ? (
                        <span className="badge badge-danger">ملغي</span>
                      ) : (
                        <span className="badge badge-info">قيد الانتظار</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.8125rem', color: 'var(--gray-600)', marginBottom: '0.5rem' }}>تاريخ البدء</div>
                    <div style={{ fontWeight: 500 }}>{new Date(subscription.startDate).toLocaleDateString('ar-SA')}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.8125rem', color: 'var(--gray-600)', marginBottom: '0.5rem' }}>تاريخ الانتهاء</div>
                    <div style={{ fontWeight: 500 }}>{new Date(subscription.endDate).toLocaleDateString('ar-SA')}</div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="empty-state">
                <div className="empty-state-icon">📋</div>
                <div className="empty-state-title">لا يوجد اشتراك نشط</div>
                <div className="empty-state-text" style={{ marginTop: '1rem' }}>
                  <button className="btn btn-primary" onClick={() => setActiveTab('create-subscription')}>
                    إضافة اشتراك جديد
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Create Subscription Tab - shown when no subscription */}
        {activeTab === 'create-subscription' && (
          <div>
            <CreateSubscription
              customerId={customer._id}
              onSubscriptionCreated={(newSubscription) => {
                setSubscription(newSubscription);
                setActiveTab('subscription');
                if (onRefresh) onRefresh();
              }}
              onCancel={() => setActiveTab('subscription')}
            />
          </div>
        )}

        {/* Logs Tab */}
        {activeTab === 'logs' && (
          <div>
            <h3 className="card-title" style={{ marginBottom: '1.5rem' }}>سجل الاستخدام</h3>
            {loading ? (
              <div className="loading">
                <div className="loading-spinner"></div>
                جاري التحميل...
              </div>
            ) : usageLogs.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">📝</div>
                <div className="empty-state-title">لا يوجد سجل استخدام</div>
              </div>
            ) : (
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>الخدمة</th>
                      <th>التكلفة</th>
                      <th>حالة الاستجابة</th>
                      <th>التاريخ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usageLogs.map((log) => (
                      <tr key={log._id}>
                        <td>
                          {log.service === 'AI_IMAGE' ? 'توليد الصور' : 
                           log.service === 'SMS' ? 'الرسائل النصية' : 
                           log.service === 'OPENAI' ? 'OpenAI' : log.service}
                        </td>
                        <td><strong>{log.cost}</strong></td>
                        <td>
                          <span className={`badge ${log.responseStatus === 200 ? 'badge-success' : 'badge-danger'}`}>
                            {log.responseStatus}
                          </span>
                        </td>
                        <td>{new Date(log.createdAt).toLocaleString('ar-SA')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Transactions Tab */}
        {activeTab === 'transactions' && (
          <div>
            <h3 className="card-title" style={{ marginBottom: '1.5rem' }}>المعاملات</h3>
            {transactions.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">💳</div>
                <div className="empty-state-title">لا يوجد معاملات</div>
              </div>
            ) : (
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>النوع</th>
                      <th>نوع الرصيد</th>
                      <th>الخدمة</th>
                      <th>المبلغ</th>
                      <th>الرصيد قبل</th>
                      <th>الرصيد بعد</th>
                      <th>المرجع</th>
                      <th>التاريخ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((transaction) => (
                      <tr key={transaction._id}>
                        <td>
                          <span className={`badge ${transaction.type === 'CREDIT' ? 'badge-success' : 'badge-danger'}`}>
                            {transaction.type === 'CREDIT' ? '➕ إضافة' : '➖ خصم'}
                          </span>
                        </td>
                        <td>
                          <span className="badge badge-info">
                            {transaction.creditType === 'aiCredits' ? 'AI' : 
                             transaction.creditType === 'smsCredits' ? 'SMS' :
                             transaction.creditType === 'openaiBalance' ? 'OpenAI' : transaction.creditType}
                          </span>
                        </td>
                        <td>
                          {transaction.service === 'AI_IMAGE' ? 'توليد الصور' : 
                           transaction.service === 'SMS' ? 'الرسائل النصية' : 
                           transaction.service === 'OPENAI' ? 'OpenAI' :
                           transaction.service === 'MANUAL' ? 'يدوي' : transaction.service}
                        </td>
                        <td><strong>{transaction.amount}</strong></td>
                        <td>{transaction.balanceBefore}</td>
                        <td><strong>{transaction.balanceAfter}</strong></td>
                        <td>
                          {transaction.reference ? (
                            <span style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>
                              {transaction.reference}
                            </span>
                          ) : (
                            <span style={{ color: 'var(--gray-400)', fontStyle: 'italic' }}>—</span>
                          )}
                        </td>
                        <td style={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                          {new Date(transaction.createdAt).toLocaleString('ar-SA')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Configuration Tab */}
        {activeTab === 'configuration' && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '2rem' }}>
              {/* Basic Information Card */}
              <div className="card">
                <h4 style={{ marginBottom: '1.5rem', color: 'var(--primary-600)', fontSize: '1.125rem', fontWeight: 600, paddingBottom: '0.75rem', borderBottom: '2px solid var(--gray-200)' }}>
                  المعلومات الأساسية
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <InfoField label="الاسم" value={customerData.name} />
                  <InfoField label="البريد الإلكتروني" value={customerData.email} />
                  <InfoField label="نوع النشاط" value={
                    customerData.businessType === 'electronics' ? 'إلكترونيات' :
                    customerData.businessType === 'clothes' ? 'ملابس' :
                    customerData.businessType === 'home-accessories' ? 'إكسسوارات منزلية' : customerData.businessType
                  } />
                  <InfoField label="OpenAI Project ID" value={customerData.openaiProjectId} code />
                  {customerData.vps && (
                    <InfoField label="خادم VPS" value={customerData.vps?.ip || customerData.vps} />
                  )}
                  {customerData.createdAt && (
                    <InfoField label="تاريخ الإنشاء" value={new Date(customerData.createdAt).toLocaleString('ar-SA')} />
                  )}
                  {customerData.updatedAt && (
                    <InfoField label="آخر تحديث" value={new Date(customerData.updatedAt).toLocaleString('ar-SA')} />
                  )}
                </div>
              </div>

              {/* Store Names & Descriptions Card */}
              <div className="card">
                <h4 style={{ marginBottom: '1.5rem', color: 'var(--primary-600)', fontSize: '1.125rem', fontWeight: 600, paddingBottom: '0.75rem', borderBottom: '2px solid var(--gray-200)' }}>
                  أسماء ووصف المتجر
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <InfoField label="اسم المتجر (إنجليزي)" value={customerData.storeNameEn} />
                  <InfoField label="اسم المتجر (عربي)" value={customerData.storeNameAr} />
                  <InfoField label="الاسم المختصر (EN)" value={customerData.storeNameShortEn} />
                  <InfoField label="الاسم المختصر (AR)" value={customerData.storeNameShortAr} />
                  <InfoField label="الشعار (EN)" value={customerData.storeTaglineEn} />
                  <InfoField label="الشعار (AR)" value={customerData.storeTaglineAr} />
                  <InfoField label="الوصف (EN)" value={customerData.storeDescriptionEn} multiline />
                  <InfoField label="الوصف (AR)" value={customerData.storeDescriptionAr} multiline />
                </div>
              </div>

              {/* Contact Information Card */}
              <div className="card">
                <h4 style={{ marginBottom: '1.5rem', color: 'var(--primary-600)', fontSize: '1.125rem', fontWeight: 600, paddingBottom: '0.75rem', borderBottom: '2px solid var(--gray-200)' }}>
                  معلومات الاتصال
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <InfoField label="بريد المعلومات" value={customerData.storeEmailInfo} email />
                  <InfoField label="بريد الدعم" value={customerData.storeEmailSupport} email />
                  <InfoField label="بريد الإدارة" value={customerData.storeEmailAdmin} email />
                  <InfoField label="بريد الخصوصية" value={customerData.storeEmailPrivacy} email />
                  <InfoField label="الهاتف الأساسي" value={customerData.storePhonePrimary} />
                  <InfoField label="الهاتف الثانوي" value={customerData.storePhoneSecondary} />
                </div>
              </div>

              {/* Address Information Card */}
              <div className="card">
                <h4 style={{ marginBottom: '1.5rem', color: 'var(--primary-600)', fontSize: '1.125rem', fontWeight: 600, paddingBottom: '0.75rem', borderBottom: '2px solid var(--gray-200)' }}>
                  العنوان
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <InfoField label="العنوان (EN)" value={customerData.storeAddressEn} multiline />
                  <InfoField label="العنوان (AR)" value={customerData.storeAddressAr} multiline />
                  <InfoField label="السطر الثاني" value={customerData.storeAddressLine2} />
                  <InfoField label="المدينة" value={customerData.storeCity} />
                  <InfoField label="الدولة" value={customerData.storeCountry} />
                </div>
              </div>

              {/* URLs Card */}
              <div className="card">
                <h4 style={{ marginBottom: '1.5rem', color: 'var(--primary-600)', fontSize: '1.125rem', fontWeight: 600, paddingBottom: '0.75rem', borderBottom: '2px solid var(--gray-200)' }}>
                  الروابط
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <InfoField label="API URL" value={customerData.apiUrl} url code />
                  <InfoField label="رابط الواجهة الأمامية" value={customerData.storeFrontendUrl} url />
                </div>
              </div>

              {/* Store Hours Card */}
              <div className="card">
                <h4 style={{ marginBottom: '1.5rem', color: 'var(--primary-600)', fontSize: '1.125rem', fontWeight: 600, paddingBottom: '0.75rem', borderBottom: '2px solid var(--gray-200)' }}>
                  ساعات العمل
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <InfoField label="أيام الأسبوع (EN)" value={customerData.storeHoursWeekdaysEn} />
                  <InfoField label="أيام الأسبوع (AR)" value={customerData.storeHoursWeekdaysAr} />
                  <InfoField label="نهاية الأسبوع (EN)" value={customerData.storeHoursWeekendEn} />
                  <InfoField label="نهاية الأسبوع (AR)" value={customerData.storeHoursWeekendAr} />
                </div>
              </div>

              {/* Brand Colors Card */}
              <div className="card">
                <h4 style={{ marginBottom: '1.5rem', color: 'var(--primary-600)', fontSize: '1.125rem', fontWeight: 600, paddingBottom: '0.75rem', borderBottom: '2px solid var(--gray-200)' }}>
                  ألوان العلامة التجارية
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                  {customerData.storePrimaryColor && (
                    <ColorField label="اللون الأساسي" color={customerData.storePrimaryColor} hsl={customerData.storePrimaryColorHsl} />
                  )}
                  {customerData.storeSecondaryColor && (
                    <ColorField label="اللون الثانوي" color={customerData.storeSecondaryColor} hsl={customerData.storeSecondaryColorHsl} />
                  )}
                  {customerData.storeAccentColor && (
                    <ColorField label="لون التمييز" color={customerData.storeAccentColor} hsl={customerData.storeAccentColorHsl} />
                  )}
                  {customerData.storeBackgroundColor && (
                    <ColorField label="لون الخلفية" color={customerData.storeBackgroundColor} hsl={customerData.storeBackgroundColorHsl} />
                  )}
                </div>
              </div>

              {/* Coming Soon Card */}
              <div className="card">
                <h4 style={{ marginBottom: '1.5rem', color: 'var(--primary-600)', fontSize: '1.125rem', fontWeight: 600, paddingBottom: '0.75rem', borderBottom: '2px solid var(--gray-200)' }}>
                  صفحة قريباً
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <InfoField label="الوصف (EN)" value={customerData.storeComingSoonDescriptionEn} multiline />
                  <InfoField label="الوصف (AR)" value={customerData.storeComingSoonDescriptionAr} multiline />
                  <InfoField label="الشعار (EN)" value={customerData.storeComingSoonTaglineEn} />
                  <InfoField label="الشعار (AR)" value={customerData.storeComingSoonTaglineAr} />
                </div>
              </div>

              {/* MongoDB Configuration Card */}
              <div className="card">
                <h4 style={{ marginBottom: '1.5rem', color: 'var(--primary-600)', fontSize: '1.125rem', fontWeight: 600, paddingBottom: '0.75rem', borderBottom: '2px solid var(--gray-200)' }}>
                  إعدادات MongoDB
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <InfoField label="MongoDB URI" value={customerData.mongodbUri} code secret copyable />
                  <InfoField label="اسم قاعدة البيانات" value={customerData.mongodbDbName} />
                </div>
              </div>

              {/* R2 / Cloudflare Card */}
              <div className="card">
                <h4 style={{ marginBottom: '1.5rem', color: 'var(--primary-600)', fontSize: '1.125rem', fontWeight: 600, paddingBottom: '0.75rem', borderBottom: '2px solid var(--gray-200)' }}>
                  إعدادات R2 / Cloudflare
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <InfoField label="Access Key ID" value={customerData.r2AccessKeyId} code secret copyable />
                  <InfoField label="Secret Access Key" value={customerData.r2SecretAccessKey} code secret copyable />
                  <InfoField label="اسم الـ Bucket" value={customerData.r2BucketName} />
                  <InfoField label="Public URL" value={customerData.r2PublicUrl} url />
                  <InfoField label="Endpoint" value={customerData.r2Endpoint} code copyable />
                  <InfoField label="Public Base URL" value={customerData.r2PublicBaseUrl} url />
                </div>
              </div>

              {/* Telegram Configuration Card */}
              <div className="card">
                <h4 style={{ marginBottom: '1.5rem', color: 'var(--primary-600)', fontSize: '1.125rem', fontWeight: 600, paddingBottom: '0.75rem', borderBottom: '2px solid var(--gray-200)' }}>
                  إعدادات Telegram
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <InfoField label="Bot Token" value={customerData.telegramBotToken} code secret copyable />
                  <InfoField label="Bot Username" value={customerData.telegramBotUsername} />
                </div>
              </div>

              {/* Admin Configuration Card */}
              <div className="card">
                <h4 style={{ marginBottom: '1.5rem', color: 'var(--primary-600)', fontSize: '1.125rem', fontWeight: 600, paddingBottom: '0.75rem', borderBottom: '2px solid var(--gray-200)' }}>
                  بيانات الإدارة الافتراضية
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <InfoField label="البريد الإلكتروني" value={customerData.adminDefaultEmail} email />
                  <InfoField label="كلمة المرور" value={customerData.adminDefaultPassword} secret copyable />
                  <InfoField label="الاسم" value={customerData.adminDefaultName} />
                  <InfoField label="الهاتف" value={customerData.adminDefaultPhone} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Checklist Tab */}
        {activeTab === 'checklist' && (
          <div>
            <SetupChecklist
              customerId={customer._id}
              checklist={customerData.setupChecklist || []}
              onUpdate={(updatedData) => {
                setCustomerData(updatedData);
                if (onRefresh) onRefresh();
              }}
            />
          </div>
        )}
      </div>

      <EnvModal
        isOpen={envModal.isOpen}
        onClose={() => setEnvModal({ isOpen: false, title: '', content: '' })}
        title={envModal.title}
        content={envModal.content}
      />
    </div>
  );
};

export default CustomerDetails;

